/**
 * Fullscreen product-media viewer (opened by tapping a product photo).
 *
 * Layout, top → bottom:
 *   - plain X (top-left, no chrome) · "4/8" position (top-center)
 *   - swipeable media (photos + clips) — reuses ProductCarousel
 *   - the active variant picker (swatches + selected label)
 *   - the primary CTA (Add to cart / wishlist), in the shop's theme colour
 *
 * Open/close is a clean symmetric fade + subtle rise — NOT a zoom-to-thumbnail
 * (that collapsed the current photo into the page carousel's spot and looked
 * bad). Dragging the media DOWN dismisses it: the sheet follows the finger and
 * fades the backdrop, then on release past a threshold it slides out, otherwise
 * it snaps back.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import ProductCarousel from '../ProductCarousel';
import useDialogA11y from '../../hooks/useDialogA11y';
import styles from './styles.module.css';

const CLOSE_DRAG_PX = 120;
const ANIM_MS = 280;

const ProductMediaViewer = ({
  open,
  onClose,
  images,
  index,
  onIndexChange,
  alt = '',
  // Variant picker for the active axis (color OR type). [] = no picker.
  options = [],
  optionLabel = '',
  selectedLabel = '',
  // Primary CTA.
  onPrimary,
  primaryLabel,
  primaryIcon = null,
  primaryDisabled = false,
  // Where focus goes home to. Optional: without it the hook falls back to
  // whatever had focus when the viewer opened, which is the thumbnail in every
  // path that exists today.
  returnFocusRef = null,
}) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [dragClosing, setDragClosing] = useState(false);
  const dragRef = useRef(null);
  const overlayRef = useRef(null);
  const viewportH = useRef(typeof window !== 'undefined' ? window.innerHeight : 900);

  /* AN OPAQUE OVERLAY IS NOT A CLOSED DOOR.
   *
   * The viewer covers the page in black and the page kept its tab stops: six
   * Tabs from in here reached the product's own "add to cart" button behind
   * the photo, and Enter really did add the item — silently, to a shopper who
   * is looking at a picture. Escape already worked; the containment, the way
   * in and the way back never existed.
   *
   * `open && mounted`, and both halves are load-bearing. `mounted` alone would
   * keep the trap on through the 280ms exit fade, so focus would come home a
   * third of a second after the shopper asked to leave. `open` alone is FALSE
   * on the first render of an opening viewer — `mounted` is still false, so
   * nothing is rendered and the ref is empty — and the effect would never run
   * again, which is a focus trap that silently does nothing. */
  useDialogA11y({
    active: open && mounted,
    containerRef: overlayRef,
    onEscape: onClose,
    returnFocusRef,
  });

  useEffect(() => {
    if (open) {
      viewportH.current = typeof window !== 'undefined' ? window.innerHeight : 900;
      setMounted(true);
      setDragY(0);
      setDragging(false);
      setDragClosing(false);
      setShown(false);
      // Two frames so the browser paints the hidden state before we flip to
      // shown — otherwise the enter transition is skipped.
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    if (mounted) {
      setShown(false);
      const t = setTimeout(() => setMounted(false), ANIM_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock the page scroll behind the viewer while it's up.
  useEffect(() => {
    if (!mounted) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mounted]);

  // Keyboard navigation: ←/→ move photos (reading-direction aware), Esc closes.
  // A no-op with a single image, and for a key pointing past the first or the last
  // photo — the photos stop at both ends, as the page's gallery and the card's do
  // (they used to wrap: "back" on photo 1 opened the last one). Only bound while
  // the viewer is open.
  useEffect(() => {
    if (!open) return undefined;
    const count = Array.isArray(images) ? images.length : 0;
    const isRtl = typeof document !== 'undefined'
      && (document.documentElement?.dir === 'rtl' || document.body?.dir === 'rtl');
    const step = (dir) => {
      const target = index + dir;
      if (count <= 1 || !onIndexChange || target < 0 || target > count - 1) return;
      onIndexChange(target);
    };
    // Escape belongs to `useDialogA11y` now — one dialog, one Escape, and the
    // topmost dialog is the one that answers it.
    const onKey = (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); step(isRtl ? -1 : 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); step(isRtl ? 1 : -1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, images, index, onIndexChange]);

  if (!mounted) return null;

  // Reading-direction-aware prev/next (desktop hover arrows).
  //
  // The two buttons are placed with LOGICAL properties — `.navPrev` is
  // `inset-inline-start`, so in Hebrew it is already on the right, where the
  // previous photo belongs. Only the GLYPH still has to be chosen, because a
  // chevron is a physical shape and `inset-inline-start` cannot rotate it.
  // The step must NOT be flipped as well: doing both put the button standing
  // in "previous"'s place on a forward step, and aimed the two arrows at each
  // other. The page's own carousel next door follows this same rule.
  const isRtl = typeof document !== 'undefined'
    && (document.documentElement?.dir === 'rtl' || document.body?.dir === 'rtl');
  const Backward = isRtl ? FiChevronRight : FiChevronLeft;
  const Forward = isRtl ? FiChevronLeft : FiChevronRight;
  const go = (dir) => {
    const count = Array.isArray(images) ? images.length : 0;
    const target = index + dir;
    if (count <= 1 || !onIndexChange || target < 0 || target > count - 1) return;
    onIndexChange(target);
  };

  const onTouchStart = (e) => {
    // A second finger = pinch-to-zoom — cancel any drag-to-dismiss.
    if (e.touches.length > 1) { dragRef.current = null; return; }
    const t = e.touches?.[0];
    if (!t) return;
    dragRef.current = { x: t.clientX, y: t.clientY, active: false };
  };
  const onTouchMove = (e) => {
    if (e.touches.length > 1) { dragRef.current = null; return; }
    const s = dragRef.current;
    if (!s) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (!s.active) {
      // Only claim a clearly-downward pull — horizontal swipes belong to the
      // carousel underneath.
      if (dy > 8 && dy > Math.abs(dx)) { s.active = true; setDragging(true); }
      else if (Math.abs(dx) > Math.abs(dy)) return;
    }
    if (s.active && dy > 0) setDragY(dy);
  };
  const onTouchEnd = () => {
    const s = dragRef.current;
    dragRef.current = null;
    if (!s?.active) return;
    setDragging(false);
    if (dragY > CLOSE_DRAG_PX) { setDragClosing(true); onClose(); }
    else setDragY(0);
  };

  // Transform: live drag wins; entering/idle sits at 0; exit either slides the
  // rest of the way down (drag-dismiss) or gently drops 16px (X / backdrop).
  let transform;
  if (dragging) transform = `translateY(${dragY}px)`;
  else if (shown) transform = 'translateY(0)';
  else transform = dragClosing ? `translateY(${viewportH.current}px)` : 'translateY(16px)';

  const opacity = dragging
    ? Math.max(0, 1 - dragY / (viewportH.current || 900))
    : (shown ? 1 : 0);
  const transition = dragging
    ? 'none'
    : `opacity ${ANIM_MS}ms ease, transform ${ANIM_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
  const total = Array.isArray(images) ? images.length : 0;

  return createPortal(
    <div
      ref={overlayRef}
      className={styles.overlay}
      style={{ opacity, transition }}
      role="dialog"
      aria-modal="true"
      /* The exit is a fade, so "gone" and "leaving" are different states and a
       * test that could only see the document could not tell them apart. */
      data-open={open ? 'true' : 'false'}
      data-testid="product-media-viewer"
    >
      <div className={styles.sheet} style={{ transform, transition }}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            data-testid="product-media-viewer-close"
          >
            <FiX />
          </button>
          {total > 1 && (
            <span className={styles.counter} data-testid="product-media-viewer-counter">
              {index + 1}/{total}
            </span>
          )}
          <span className={styles.topBarSpacer} aria-hidden />
        </div>

        <div
          className={styles.media}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <ProductCarousel
            images={images}
            alt={alt}
            index={index}
            onIndexChange={onIndexChange}
            zoomable
            hideOverlays
            fitContain
            transparentBg
            videoAutoplay
            videoTapToggle
          />
          {/* Desktop prev/next arrows (mouse users can't swipe). Touch users
              swipe; keyboard users use ←/→. Hidden ≤ pointer:coarse via CSS. */}
          {total > 1 && (
            <>
              {/* `aria-disabled` rather than `disabled` at an end: a button that
                  disables itself under the keyboard's focus drops that focus out of
                  the dialog. */}
              <button
                type="button"
                className={`${styles.navArrow} ${styles.navPrev}`}
                onClick={() => go(-1)}
                aria-label={t('productDetail.previousPhoto', { defaultValue: 'Previous photo' })}
                aria-disabled={index <= 0}
                data-testid="product-media-viewer-prev"
              >
                <Backward />
              </button>
              <button
                type="button"
                className={`${styles.navArrow} ${styles.navNext}`}
                onClick={() => go(1)}
                aria-label={t('productDetail.nextPhoto', { defaultValue: 'Next photo' })}
                aria-disabled={index >= total - 1}
                data-testid="product-media-viewer-next"
              >
                <Forward />
              </button>
            </>
          )}
        </div>

        <div className={styles.bottom}>
          {options.length > 1 && (
            <div className={styles.picker} data-testid="product-media-viewer-picker">
              {selectedLabel && (
                <span className={styles.selectedChip}>
                  {optionLabel ? `${optionLabel}: ` : ''}{selectedLabel}
                </span>
              )}
              <div className={styles.swatchRow}>
                {options.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`${styles.swatch} ${o.selected ? styles.swatchSelected : ''}`}
                    onClick={o.onSelect}
                    aria-label={o.label}
                    aria-pressed={o.selected}
                    title={o.label}
                  >
                    {/* A swatch with no colour to paint is a neutral ring with the value's letter, never an empty grey
                        disc (Swe Sosetta run 5, round C: S fix round 2, #2). */}
                    {o.thumb
                      ? <img src={o.thumb} alt="" className={styles.swatchImg} />
                      : o.swatch
                        ? <span className={styles.swatchColor} style={{ background: o.swatch }} data-swatch="colour" />
                        : <span className={`${styles.swatchColor} ${styles.swatchNeutral}`} data-swatch="neutral" aria-hidden>{o.letter || ''}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {primaryLabel && (
            <button
              type="button"
              className={styles.cta}
              onClick={onPrimary}
              disabled={primaryDisabled}
              data-testid="product-media-viewer-cta"
            >
              {primaryIcon}
              {primaryLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ProductMediaViewer;
